import type { User } from '@feathr/blackbox';
import { Session } from '@feathr/blackbox';

import { assertAuthenticatedStore, useStore } from '.';

class NoUserError extends Error {
  constructor(message = 'No user context available.') {
    const trueProto = new.target.prototype;
    super(message);
    Object.setPrototypeOf(this, trueProto);
  }

  public override toString(): string {
    return `NoUserError: ${this.message}`;
  }
}

export function assertUser(user: User | undefined): asserts user {
  // Only treat an errored user as "no context" when it was never fetched (a failed
  // load). A failed mutation flags isErrored but leaves a fetched, usable user in
  // place — throwing there would crash every useUser consumer.
  if (user === undefined || user.isEphemeral || (user.isErrored && !user.isFetched)) {
    throw new NoUserError();
  }
}

interface IUserOptions {
  required: boolean;
}

function useUser(): User;
function useUser(options: { required: true }): User;
function useUser(options: { required: false }): User | undefined;
function useUser(options: IUserOptions = { required: true }): User | undefined {
  const store = useStore();
  assertAuthenticatedStore(store);

  let user: User | undefined;
  const { required } = options;
  const { Users } = store;

  if (Session.userId) {
    localStorage.setItem('userId', Session.userId);
    user = Users.get(Session.userId, undefined, undefined, undefined, (user) => {
      localStorage.setItem('userName', user.name);
      localStorage.setItem('userEmail', user.get('email'));
    });
  }

  if (required) {
    assertUser(user);
  }

  return user;
}

export default useUser;
export { NoUserError };
